import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Archive, ArrowLeft, Building2, IdCard, LayoutDashboard, LayoutGrid, LogOut, UserCog, Users } from "lucide-react";
import { api, useAuth } from "@/auth/AuthContext";
import PTTaskList from "@/pages/PTTaskList";
import { PTProfileReadOnly } from "@/pages/PTProfileReadOnly";

/**
 * PT Operation Desk. Renders identity + placeholder for future data collection.
 * - When accessed by PT user (own account): shows their own record.
 * - When accessed by master/admin via /kipk/pt/:id: read-only view of that PT.
 */
export default function PTDashboard() {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pt, setPt] = useState(null);
  const [profile, setProfile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewTab, setPreviewTab] = useState("dashboard");
  const [error, setError] = useState("");
  const isAdminViewer = user && (user.role === "master" || user.role === "admin");
  const isPtSelf = user?.role === "pt" && !id;
  const targetId = id || user?.id;

  useEffect(() => {
    if (!targetId) return;
    setError("");
    if (isAdminViewer) {
      api.get(`/admin/pt-preview/${encodeURIComponent(targetId)}`)
        .then((r) => { setPreview(r.data); setPt(r.data.account); setProfile(r.data.profile); })
        .catch((e) => setError(e.response?.data?.detail || "Data PT tidak dapat dimuat."));
      return;
    }
    api.get(`/pt-accounts/${targetId}`)
      .then((r) => setPt(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Data PT tidak dapat dimuat."));
  }, [targetId, isAdminViewer]);

  // Load own profile status for gating
  useEffect(() => {
    if (!isPtSelf) return;
    api.get("/pt/profile").then((r) => {
      setProfile(r.data);
      if (r.data.status !== "verified") {
        navigate("/pt/profil", { replace: true });
      }
    }).catch(() => {});
  }, [isPtSelf, navigate]);

  if (error) return <div className="loading-screen">{error}</div>;
  if (!pt) return <div className="loading-screen">Memuat data PT…</div>;

  return (
    <div className="pt-shell">
      <header className="pt-header">
        <div className="pt-header-brand">
          <img src="/logo-kemdikbud.png" alt="Logo Kemendikbud" className="login-logo" />
          <div>
            <div className="eyebrow">OPERATION DESK</div>
            <h1 data-testid="pt-title">{pt.namapt}</h1>
            <p className="subtitle">Kode PT: <span className="mono">{pt.kodept}</span></p>
          </div>
        </div>
        <div className="pt-header-actions">
          {isAdminViewer && (
            <Link to="/kipk/manajemen-pt" className="download-button ghost" data-testid="back-to-admin"><ArrowLeft size={14} /> Kembali ke Manajemen PT</Link>
          )}
          {!isAdminViewer && (
            <>
              <Link to="/padma" className="download-button ghost" data-testid="pt-goto-padma"><LayoutGrid size={14} /> Portal PADMA</Link>
              <Link to="/pt/profil" className="download-button ghost" data-testid="pt-goto-profil"><UserCog size={14} /> Profil Pengelola</Link>
              <Link to="/pt/mahasiswa" className="download-button ghost" data-testid="pt-goto-students"><Users size={14} /> Data Mahasiswa</Link>
              <Link to="/pt/arsip-task" className="download-button ghost" data-testid="pt-goto-task-archive"><Archive size={14} /> Arsip Task</Link>
              <button className="download-button ghost" data-testid="pt-logout" onClick={logout}><LogOut size={14} /> Keluar</button>
            </>
          )}
        </div>
      </header>

      {isAdminViewer && (
        <div className="info-banner" data-testid="pt-readonly-banner">
          <Building2 size={14} /> Anda sedang melihat halaman <b>{pt.namapt}</b> sebagai admin (read-only). Isian tidak dapat diubah.
        </div>
      )}

      {isAdminViewer && (
        <div className="tab-strip pt-preview-tabs" data-testid="pt-preview-tabs">
          <button className={`tab-button ${previewTab === "dashboard" ? "active" : ""}`} data-testid="pt-preview-dashboard-tab" onClick={() => setPreviewTab("dashboard")}>
            <LayoutDashboard size={14} /> Dashboard & Task
          </button>
          <button className={`tab-button ${previewTab === "profile" ? "active" : ""}`} data-testid="pt-preview-profile-tab" onClick={() => setPreviewTab("profile")}>
            <UserCog size={14} /> Profil Pengelola
          </button>
        </div>
      )}

      {(!isAdminViewer || previewTab === "dashboard") && (
        <>
          <section className="pt-card">
            <div className="eyebrow"><IdCard size={13} /> IDENTITAS</div>
            <h2>Profil Perguruan Tinggi</h2>
            <div className="pt-identity-grid">
              <div><span>Nama Perguruan Tinggi</span><strong data-testid="pt-nama">{pt.namapt}</strong></div>
              <div><span>Kode Perguruan Tinggi</span><strong className="mono" data-testid="pt-kode">{pt.kodept}</strong></div>
              <div><span>Username akun</span><strong className="mono">{pt.username}</strong></div>
              <div><span>Terdaftar sejak</span><strong className="mono">{pt.created_at?.slice(0, 10) || "—"}</strong></div>
            </div>
          </section>
          <section className="pt-task-section">
            <PTTaskList previewData={isAdminViewer ? preview : null} readOnly={isAdminViewer} />
          </section>
        </>
      )}
      {isAdminViewer && previewTab === "profile" && <PTProfileReadOnly profile={profile} pt={pt} />}
    </div>
  );
}
